import {
  LineChart,
  Line,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  TooltipProps,
} from "recharts";
import { CHART_COLORS } from "./chartTokens";
import { InformationHoverIcon } from "./InformationHoverIcon";

export type BasicPipelineDailyDatum = {
  date: string;
  totalLeads: number;
  successCount: number;
};

type Props = {
  title?: string;
  subtitle?: string;
  info?: string;
  colSpan?: number;
  data: BasicPipelineDailyDatum[];
  totalLabel?: string;
  successLabel: string;
};

const SUCCESS_COLOR = "#0D9488";

const CustomTooltip = ({
  active,
  payload,
  label,
  totalLabel,
  successLabel,
}: TooltipProps<number, string> & { totalLabel: string; successLabel: string }) => {
  if (!active || !payload || payload.length === 0) return null;
  const row = payload[0].payload as BasicPipelineDailyDatum;
  return (
    <div className="rounded-md border border-border bg-card p-3 shadow-md">
      <p className="text-sm font-semibold text-palomma-primary">{label}</p>
      <p className="text-sm text-palomma-secondary">
        {totalLabel}: {row.totalLeads}
      </p>
      <p className="text-sm text-palomma-secondary">
        {successLabel}: {row.successCount}
      </p>
    </div>
  );
};

const BasicPipelineDailyChart = ({
  title = "Evolución diaria",
  subtitle = "Conversaciones y resultados exitosos por día",
  info,
  colSpan = 1,
  data,
  totalLabel = "Conversaciones",
  successLabel,
}: Props) => {
  const series = [
    { dataKey: "totalLeads", name: totalLabel, color: CHART_COLORS.primary },
    { dataKey: "successCount", name: successLabel, color: SUCCESS_COLOR },
  ];

  // Rows are zero-filled server-side, so emptiness means "no day had activity".
  const hasData = data.some((row) => row.totalLeads > 0 || row.successCount > 0);

  return (
    <div style={{ gridColumn: `span ${colSpan}` }}>
      <div className="h-full rounded-2xl border border-border bg-card p-6">
        <div className="mb-4">
          <div className="flex items-center gap-1.5">
            <h3 className="text-sm font-semibold text-palomma-secondary">{title}</h3>
            {info && <InformationHoverIcon information={info} />}
          </div>
          {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
        </div>

        {!hasData ? (
          <div className="flex h-[260px] items-center justify-center text-sm text-muted-foreground">
            Sin datos en este período
          </div>
        ) : (
          <>
            <div className="h-[260px] md:h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data} margin={{ top: 20, right: 30, left: 0, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.3} />
                  <XAxis
                    dataKey="date"
                    tick={{ fill: CHART_COLORS.axis, fontSize: 12 }}
                    tickMargin={10}
                    axisLine={{ stroke: CHART_COLORS.tooltipBorder }}
                  />
                  <YAxis
                    tick={{ fill: CHART_COLORS.axis, fontSize: 12 }}
                    axisLine={{ stroke: CHART_COLORS.tooltipBorder }}
                    allowDecimals={false}
                    width={32}
                  />
                  <Tooltip
                    content={<CustomTooltip totalLabel={totalLabel} successLabel={successLabel} />}
                  />
                  {series.map((s) => (
                    <Line
                      key={s.dataKey}
                      type="monotone"
                      dataKey={s.dataKey}
                      name={s.name}
                      stroke={s.color}
                      strokeWidth={2}
                      dot={{ r: 3, fill: s.color }}
                      activeDot={{ r: 5 }}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div className="mt-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
              {series.map((s) => (
                <div key={s.dataKey} className="flex items-center">
                  <div
                    className="mr-2 size-2.5 rounded-full"
                    style={{ backgroundColor: s.color }}
                  />
                  <p className="text-sm text-palomma-secondary">{s.name}</p>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default BasicPipelineDailyChart;
